import { Tabs } from "antd";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { I18N_NS } from "Config/i18n";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { BcfEditIssueForm } from "./BcfEditIssueForm";
import { BcfIssueComments } from "./BcfIssueComments";
import { BcfIssueEvents } from "./BcfIssueEvents";
import { ViewpointManager } from "./ViewpointManager";
import { BCFModels } from "Pages/Project/BIM/Components/BCFModels";
import { BCFAttachmentsTable } from "Pages/Project/BIM/Components/BCFAttachmentsTable";
import { BcfServerTopicWithModelIdAndDeleted, BCFStore } from "Components/XKTViewer/Stores/BCFStore";
import { useModelSaveCoordination } from "Pages/Project/BIM/Components/hooks/useModelSaveCoordination";

interface BcfEditIssueProps extends ModalComponentProps {
  topic: BcfServerTopicWithModelIdAndDeleted;
  bcfStore: BCFStore;
  viewpointManager: ViewpointManager;
  onEnterAddViewpointMode: () => void;
  isTopicDisabled?: boolean;
}

export const BcfEditIssue = ({
  bcfStore,
  topic,
  hideModal,
  setConfirmOnCancel,
  setOnSave,
  onEnterAddViewpointMode,
  viewpointManager,
  isTopicDisabled,
}: BcfEditIssueProps) => {
  const { t } = useTranslation(I18N_NS.XKT_VIEWER);
  const isEnteringViewpointModeRef = React.useRef(false);

  const { registerModelSave, wrappedSetOnSave } = useModelSaveCoordination(setOnSave);

  React.useEffect(() => {
    viewpointManager.init(topic.guid);
    return () => {
      if (!isEnteringViewpointModeRef.current) {
        viewpointManager.cancelPendingViewpoints();
      }
      isEnteringViewpointModeRef.current = false;
    };
  }, [topic.guid]);

  const handleEnterAddViewpointMode = React.useCallback(() => {
    isEnteringViewpointModeRef.current = true;
    setConfirmOnCancel?.(false);
    hideModal();
    onEnterAddViewpointMode();
  }, [hideModal, onEnterAddViewpointMode, setConfirmOnCancel]);

  const topicActionsMap =
    topic.authorization?.topic_actions?.reduce((obj, action) => {
      obj[action] = true;
      return obj;
    }, {}) || {};

  const projectActionsMap =
    bcfStore.extensions?.project_actions?.reduce((obj, action) => {
      obj[action] = true;
      return obj;
    }, {}) || {};

  const items = [
    {
      label: t("Issue"),
      key: "issue",
      children: (
        <BcfEditIssueForm
          bcfStore={bcfStore}
          topic={topic}
          hideModal={hideModal}
          setConfirmOnCancel={setConfirmOnCancel}
          setOnSave={wrappedSetOnSave}
          viewpointManager={viewpointManager}
          onEnterAddViewpointMode={handleEnterAddViewpointMode}
          isTopicDisabled={isTopicDisabled}
        />
      ),
    },
    {
      label: t("Comments"),
      key: "comments",
      children: (
        <BcfIssueComments
          topicId={topic.guid}
          topicActions={topicActionsMap}
          topicDeleted={isTopicDisabled}
          viewpointManager={viewpointManager}
        />
      ),
    },
    {
      label: t("History"),
      key: "events",
      children: <BcfIssueEvents topicId={topic.guid} />,
    },
    {
      label: t("Models"),
      key: "models",
      children: (
        <BCFModels
          topicGuid={topic.guid}
          topicActions={topicActionsMap}
          projectActions={projectActionsMap}
          topicDeleted={isTopicDisabled}
          setConfirmOnCancel={setConfirmOnCancel}
          registerModelSave={registerModelSave}
        />
      ),
    },
    {
      label: t("Attachments"),
      key: "attachments",
      children: (
        <BCFAttachmentsTable
          topicGuid={topic.guid}
          projectActions={projectActionsMap}
          topicDeleted={isTopicDisabled}
          hideModal={hideModal}
        />
      ),
    },
  ];
  return <Tabs items={items} />;
};
